import { removeToken } from "../auth"


/**
 * Fetches data from the application's backend API with  JWT authentication.
 *
 * @param {string} params - URL path segment appended to the base API endpoint.
 * @param {string} [query=''] - Optional query string appended to the request URL.
 * @returns {Promise<any>} A promise that resolves with the JSON-parsed response body.
 *
 * @description
 * Performs an authenticated GET request to the backend API defined by
 * `REACT_APP_APP_API_URL`.  
 * Automatically includes a `Bearer` token retrieved from `localStorage`
 * using the `"token"` key.
 *
 * The final request URL is constructed as:
 * `BASE_URL + params + '?' + query`
 *
 * Example:
 * ```js
 * const data = await fetchAppAPI('/analytics/data', 'range=1Y');
 * console.log(data);
 * ```
 *
 * @throws {Error} If the HTTP response status is not OK (non-2xx).
 */
export const fetchAppAPI = async (params, query = '', { auth = true, method = "GET", body } = {}) => {
    
    // Base header definition to support dynamic method request.
    let headers = { "Content-Type": "application/json" }    
    
    if (auth) {
        const token = localStorage.getItem("token");
        headers = { ...headers, "Authorization": `Bearer ${token}` };
    } 

    const res = await fetch(
        process.env.REACT_APP_APP_API_URL + params + '?' + query,
        {
            method,
            headers,
            ...(body ? { body: JSON.stringify(body) } : {}),
        })

    const parsedRes = await res.json()

    if (!res.ok) {
        if (res.status === 401) throw new Error(parsedRes.error);
        throw new Error('Internal server error, please try again later.');
    }
    return parsedRes
}

/**
 * Fetches data from the country information API.
 *
 * @param {string} params - URL path segment appended to the base API endpoint.
 * @param {string} [query=''] - Optional query string to append to the request URL.
 * @returns {Promise<any>} A promise that resolves with the parsed JSON response.
 *
 * @description
 * Sends a GET request to the country data API defined by `REACT_APP_COUNTRY_API_URL`.
 *
 * Example usage:
 * ```js
 * const countries = await fetchCountryAPI('/countries', 'region=LatinAmerica');
 * console.log(countries);
 * ```
 *
 * @throws {Error} When the response status is not OK (non-2xx).
 */
export const fetchCountryAPI = async (params, query = '') => {
    const res = await fetch(process.env.REACT_APP_COUNTRY_API_URL + params + '?' + query)
    if (!res.ok) throw new Error('Internal server error, please try again later.');
    return await res.json()
}

/**
 * Fetches data from the World Bank API.
 *
 * @param {string} params - URL path segment appended to the World Bank base endpoint.
 * @param {string} [query=''] - Optional query string to append to the request URL.
 * @returns {Promise<any>} A promise that resolves with the parsed JSON response.
 *
 * @description
 * Retrieves international economic and development data from the World Bank API
 * defined by `REACT_APP_WB_API_URL`. 
 *
 * Example usage:
 * ```js
 * const gdpData = await fetchWorldBankAPI('/indicators/NY.GDP.MKTP.CD', 'country=CR');
 * console.log(gdpData);
 * ```
 *
 * @throws {Error} When the response status is not OK (non-2xx).
 */
export const fetchWorldBankAPI = async (params, query = '') => {
    const res = await fetch(process.env.REACT_APP_WB_API_URL + params + '?' + query)
    if (!res.ok) throw new Error('Internal server error, please try again later.');
    return await res.json()
}